import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { verifyIdentity } from "../api/account";
import { Button } from "@t-one-internal/design-system";
import { Input } from "@t-one-internal/design-system";
import { Label } from "@t-one-internal/design-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@t-one-internal/design-system";

export default function VerifyIdentity() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get("uid") || "";

  const [dateOfBirth, setDateOfBirth] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!uid) {
      setError("Missing user identifier. Please go back to the login page.");
      return;
    }

    if (!dateOfBirth) {
      setError("Please enter your date of birth.");
      return;
    }

    if (attempts >= 5) {
      setError("Too many failed attempts. Please contact your HR admin.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await verifyIdentity({
        unique_id: uid,
        date_of_birth: dateOfBirth,
      });

      if (result.success && result.reset_token) {
        navigate(`/set-password?token=${result.reset_token}`);
      } else {
        setAttempts((prev) => prev + 1);
        setError(result.error || "Verification failed. Please check your details and try again.");
      }
    } catch (error: any) {
      setAttempts((prev) => prev + 1);
      const errorMessage =
        error.response?.data?.error ||
        "Verification failed. Please check your details and try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-100 p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-1 pb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#4B45E5' }}>
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-gray-900">
            Verify Your Identity
          </CardTitle>
          <CardDescription className="text-center text-gray-600">
            Please enter your date of birth to verify your identity and set up your password.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {attempts >= 5 ? (
            <div className="text-center space-y-4">
              <p className="text-gray-600 text-sm">
                You've exceeded the maximum number of verification attempts.
                Please contact your HR administrator for assistance.
              </p>
              <Button
                onClick={() => navigate("/login")}
                variant="outline"
                className="w-full h-11"
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="uid" className="text-sm font-medium text-gray-700">
                  Employee Code / Email
                </Label>
                <Input
                  id="uid"
                  type="text"
                  value={uid}
                  disabled
                  className="h-11 bg-gray-50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dob" className="text-sm font-medium text-gray-700">
                  Date of Birth
                </Label>
                <Input
                  id="dob"
                  type="date"
                  value={dateOfBirth}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateOfBirth(e.target.value)}
                  className="h-11"
                  required
                  disabled={isSubmitting}
                />
              </div>

              {attempts > 0 && attempts < 5 && (
                <p className="text-xs text-gray-500 text-center">
                  {5 - attempts} attempt{5 - attempts !== 1 ? 's' : ''} remaining
                </p>
              )}

              <Button
                type="submit"
                className="w-full h-11"
                style={{ backgroundColor: '#4B45E5' }}
                disabled={isSubmitting || !dateOfBirth}
              >
                {isSubmitting ? "Verifying..." : "Verify & Continue"}
              </Button>
            </form>
          )}

          <div className="text-center text-sm text-gray-600">
            <button
              onClick={() => navigate("/login")}
              className="hover:underline font-medium"
              style={{ color: '#4B45E5' }}
            >
              Back to Login
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
